import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Timeline from 'react-calendar-timeline';
import 'react-calendar-timeline/dist/Timeline.scss';
import { Icon } from '@iconify/react';
import { getComponents } from '../api/componentsAPI';
import { useTableFilters } from '../utils/useTableFilters';
import './ComponentPlannerLayer.css';

const SCROLLBAR_MIDPOINT = 500;
const SCROLLBAR_MIN = 0;
const SCROLLBAR_MAX = 1000;
const ZOOMBAR_MIDPOINT = 500;
const ZOOMBAR_MIN = 0;
const ZOOMBAR_MAX = 1000;
const OCCURRENCE_BAR_DURATION_MS = 24 * 60 * 60 * 1000;
const TASK_CATEGORY_FILTER_FIELD = 'plannerTaskCategory';
const MIN_ZOOM_DURATION_MS = 60 * 60 * 1000;
const MAX_ZOOM_DURATION_MS = 365 * 24 * 60 * 60 * 1000;

const hasWorkOrderNumber = (value) => {
  if (value === null || value === undefined) return false;
  return String(value).trim() !== '';
};

const getOccurrenceTimestamp = (occurrence) => {
  const raw = occurrence?.timestamp ?? occurrence?.scheduledTimestamp ?? occurrence?.date;
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
};

const isOccurrenceForTask = (occurrenceTaskId, rowTaskId) => {
  const parsedOccurrenceTaskId = Number(occurrenceTaskId);
  const parsedRowTaskId = Number(rowTaskId);

  if (!Number.isFinite(parsedOccurrenceTaskId) || !Number.isFinite(parsedRowTaskId)) {
    return false;
  }

  return parsedOccurrenceTaskId === parsedRowTaskId;
};

const ComponentPlannerLayer = () => {
  const todayStartMs = useMemo(() => {
    const now = new Date();
    const start = new Date(now);
    start.setHours(0, 0, 0, 0);
    return start.getTime();
  }, []);

  const ganttRef = useRef(null);
  const panRef = useRef({ isPanning: false, startX: 0, startScroll: 0 });
  const prevTimelineScrollValueRef = useRef(SCROLLBAR_MIDPOINT);
  const prevTimelineZoomValueRef = useRef(ZOOMBAR_MIDPOINT);
  const latestComponentRequestRef = useRef(0);
  const lastLoadedComponentQueryKeyRef = useRef('');

  const [components, setComponents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('year');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [timelineScrollValue, setTimelineScrollValue] = useState(SCROLLBAR_MIDPOINT);
  const [timelineZoomValue, setTimelineZoomValue] = useState(ZOOMBAR_MIDPOINT);
  const [visibleTime, setVisibleTime] = useState(() => {
    const now = new Date();
    const start = new Date(now);
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setMonth(end.getMonth() + 1);
    return { start: start.getTime(), end: end.getTime() };
  });
  const [sortConfig, setSortConfig] = useState({ key: 'assetNumber', direction: 'asc' });

  const clampWindowToToday = useCallback((nextStart, nextEnd, fallbackDurationMs = OCCURRENCE_BAR_DURATION_MS) => {
    const durationMs = Math.max((nextEnd - nextStart) || 0, fallbackDurationMs);
    const clampedStart = Math.max(nextStart, todayStartMs);
    return {
      start: clampedStart,
      end: clampedStart + durationMs,
    };
  }, [todayStartMs]);

  const filterConfig = useMemo(
    () => [
      {
        field: 'assetId',
        type: 'dropDown',
        label: 'Asset',
        isMulti: true,
        api: 'assets/for-dropdown',
        displayField: (asset) => `${asset.unitNumber || asset.assetNumber || ''} ${asset.assetDescription || ''}`.trim(),
        menuBarFilter: 1,
      },
      { field: 'code', type: 'dropDown', label: 'Task Code', isMulti: true, api: 'task-codes', displayField: 'name', menuBarFilter: 1 },
      {
        field: TASK_CATEGORY_FILTER_FIELD,
        type: 'dropDown',
        label: 'Task Type',
        isMulti: false,
        options: [
          { value: 'all', label: 'All' },
          { value: 'components', label: 'Components' },
          { value: 'services', label: 'Services' },
          { value: 'compliance', label: 'Compliance' },
        ],
        menuBarFilter: 1,
      },
      { field: 'assetGroupClassId', type: 'dropDown', label: 'Asset Class', api: 'class', displayField: 'className', menuBarFilter: 0 },
      { field: 'assetGroupId', type: 'dropDown', label: 'Asset Group', api: 'groups', displayField: 'groupName', menuBarFilter: 0 },
      {
        field: 'assetGroupManufacturerId',
        type: 'dropDown',
        label: 'Manufacturer',
        isMulti: true,
        api: 'manufacturers',
        displayField: 'manufacturer',
        menuBarFilter: 0,
      },
    ],
    [],
  );

  const { appliedFilterParams, renderFilterControls } = useTableFilters(filterConfig, {
    [TASK_CATEGORY_FILTER_FIELD]: 'all',
  });

  const componentQueryFilters = useMemo(() => {
    const params = { ...appliedFilterParams };
    const taskCategoryFilter = params[TASK_CATEGORY_FILTER_FIELD];
    delete params[TASK_CATEGORY_FILTER_FIELD];

    if (taskCategoryFilter === 'components') {
      params.taskType = 'components';
    } else if (taskCategoryFilter === 'services') {
      params.taskType = "service";
    } else if (taskCategoryFilter === 'compliance') {
      params.statutory = 1;
    }

    return params;
  }, [appliedFilterParams]);

  const getVisibleTimeRange = useCallback((mode = viewMode, date = currentDate) => {
    const d = new Date(date);
    let start;
    let end;

    switch (mode) {
      case 'day':
        start = new Date(d);
        start.setHours(0, 0, 0, 0);
        end = new Date(start);
        end.setDate(end.getDate() + 1);
        break;
      case 'week':
        start = new Date(d);
        start.setDate(start.getDate() - start.getDay());
        start.setHours(0, 0, 0, 0);
        end = new Date(start);
        end.setDate(end.getDate() + 7);
        break;
      case 'month':
        start = new Date(d.getFullYear(), d.getMonth(), 1);
        end = new Date(d.getFullYear(), d.getMonth() + 1, 1);
        break;
      case 'year':
        start = new Date(d.getFullYear(), 0, 1);
        end = new Date(d.getFullYear() + 1, 0, 1);
        break;
      default:
        start = new Date(d.getFullYear(), d.getMonth(), 1);
        end = new Date(d.getFullYear(), d.getMonth() + 1, 1);
        break;
    }

    const clampedStart = Math.max(start.getTime(), todayStartMs);
    if (clampedStart >= end.getTime()) {
      end = new Date(clampedStart + OCCURRENCE_BAR_DURATION_MS);
    }

    return { start: new Date(clampedStart), end };
  }, [currentDate, todayStartMs, viewMode]);

  const adjustDate = (amount) => {
    const d = new Date(currentDate);
    if (viewMode === 'day') d.setDate(d.getDate() + amount);
    else if (viewMode === 'week') d.setDate(d.getDate() + amount * 7);
    else if (viewMode === 'month') d.setMonth(d.getMonth() + amount);
    else if (viewMode === 'year') d.setFullYear(d.getFullYear() + amount);
    setCurrentDate(d);
  };

  const getTimelineScrollStepMs = (mode) => {
    switch (mode) {
      case 'day':
        return 15 * 60 * 1000;
      case 'week':
        return 60 * 60 * 1000;
      case 'month':
        return 6 * 60 * 60 * 1000;
      case 'year':
        return 24 * 60 * 60 * 1000;
      default:
        return 60 * 60 * 1000;
    }
  };

  const getViewModeDurationMs = (mode) => {
    switch (mode) {
      case 'day':
        return 24 * 60 * 60 * 1000;
      case 'week':
        return 7 * 24 * 60 * 60 * 1000;
      case 'month':
        return 30 * 24 * 60 * 60 * 1000;
      case 'year':
        return 365 * 24 * 60 * 60 * 1000;
      default:
        return 30 * 24 * 60 * 60 * 1000;
    }
  };

  const handleTimelineScrollBar = (e) => {
    const nextValue = Number(e.target.value);
    const delta = nextValue - prevTimelineScrollValueRef.current;
    prevTimelineScrollValueRef.current = nextValue;
    setTimelineScrollValue(nextValue);
    if (delta === 0) return;

    const shiftMs = delta * getTimelineScrollStepMs(viewMode);
    setVisibleTime((prev) => {
      const nextStart = prev.start + shiftMs;
      const nextEnd = prev.end + shiftMs;
      return clampWindowToToday(nextStart, nextEnd, prev.end - prev.start);
    });
  };

  const handleGanttMouseDown = (e) => {
    if (e.target.closest('.rct-item')) return;
    if (ganttRef.current) {
      panRef.current = {
        isPanning: true,
        startX: e.clientX,
        startScroll: ganttRef.current.scrollLeft,
      };
    }
  };

  const handleGanttMouseMove = (e) => {
    if (!panRef.current.isPanning || !ganttRef.current) return;
    ganttRef.current.scrollLeft = panRef.current.startScroll - (e.clientX - panRef.current.startX);
  };

  const stopGanttPan = () => {
    panRef.current.isPanning = false;
  };

  const handleTimelineZoomBar = (e) => {
    const nextValue = Number(e.target.value);
    prevTimelineZoomValueRef.current = nextValue;
    setTimelineZoomValue(nextValue);

    const normalized = (nextValue - ZOOMBAR_MIDPOINT) / ((ZOOMBAR_MAX - ZOOMBAR_MIN) / 2);
    const zoomFactor = 4 ** normalized;
    const viewModeDuration = getViewModeDurationMs(viewMode);
    const nextDuration = Math.min(
      MAX_ZOOM_DURATION_MS,
      Math.max(MIN_ZOOM_DURATION_MS, viewModeDuration * zoomFactor),
    );

    setVisibleTime((prev) => {
      const center = prev.start + (prev.end - prev.start) / 2;
      const proposedStart = center - nextDuration / 2;
      const proposedEnd = center + nextDuration / 2;
      return clampWindowToToday(proposedStart, proposedEnd, nextDuration);
    });
  };

  const toggleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key !== key) {
        return { key, direction: 'asc' };
      }
      return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
    });
  };

  useEffect(() => {
    const range = getVisibleTimeRange(viewMode, currentDate);
    setVisibleTime({ start: range.start.getTime(), end: range.end.getTime() });
    setTimelineScrollValue(SCROLLBAR_MIDPOINT);
    prevTimelineScrollValueRef.current = SCROLLBAR_MIDPOINT;
    setTimelineZoomValue(ZOOMBAR_MIDPOINT);
    prevTimelineZoomValueRef.current = ZOOMBAR_MIDPOINT;
  }, [getVisibleTimeRange, viewMode, currentDate]);

  useEffect(() => {
    window.addEventListener('mousemove', handleGanttMouseMove);
    window.addEventListener('mouseup', stopGanttPan);
    return () => {
      window.removeEventListener('mousemove', handleGanttMouseMove);
      window.removeEventListener('mouseup', stopGanttPan);
    };
  }, []);

  useEffect(() => {
    const loadAllComponents = async () => {
      const requestKey = JSON.stringify(componentQueryFilters);

      if (lastLoadedComponentQueryKeyRef.current === requestKey) {
        return;
      }

      const requestId = latestComponentRequestRef.current + 1;
      latestComponentRequestRef.current = requestId;
      lastLoadedComponentQueryKeyRef.current = requestKey;
      const isLatestRequest = () => latestComponentRequestRef.current === requestId;

      setLoading(true);
      try {
        const params = { ...componentQueryFilters };
        const pageSize = 250;
        let page = 1;
        let total = Infinity;
        const allRows = [];

        while (allRows.length < total) {
          const response = await getComponents({
            page,
            pageSize,
            combineCycleServiceTasks: false,
            combineSuppressionTasks: false,
            ...params,
          });
          const data = Array.isArray(response?.data) ? response.data : [];
          total = Number(response?.total) || data.length;
          allRows.push(...data);
          if (!data.length || data.length < pageSize) break;
          page += 1;
        }

        if (isLatestRequest()) {
          setComponents(allRows);
        }
      } catch (error) {
        if (isLatestRequest()) {
          console.error('Error loading component planner data:', error);
          setComponents([]);
          lastLoadedComponentQueryKeyRef.current = '';
        }
      } finally {
        if (isLatestRequest()) {
          setLoading(false);
        }
      }
    };

    loadAllComponents();
  }, [componentQueryFilters]);

  const { groups, items } = useMemo(() => {
    const groupedRows = [];
    const timelineItems = [];

    components.forEach((component) => {
      const groupId = String(component.id);
      const componentName = component?.name || 'Unnamed Component';
      const code = component?.code?.code || component?.code?.name || '';
      const assetNumber = component?.asset?.assetNumber || component?.asset?.unitNumber || '';
      const taskLabel = [code, componentName].filter(Boolean).join(' - ') || componentName;

      groupedRows.push({
        id: groupId,
        title: taskLabel,
        assetNumber,
        taskLabel,
      });

      const occurrences = Array.isArray(component?.usageOccurences)
        ? component.usageOccurences
        : Array.isArray(component?.usageOccurrences)
          ? component.usageOccurrences
          : [];

      occurrences.forEach((occurrence, index) => {
        if (!isOccurrenceForTask(occurrence?.taskId, component?.id)) return;

        const start = getOccurrenceTimestamp(occurrence);
        if (!start) return;

        const nowMs = Date.now();
        const originalStartMs = start.getTime();
        const isOverdue = originalStartMs < nowMs;
        const startMs = isOverdue ? nowMs : originalStartMs;

        const endMs = startMs + OCCURRENCE_BAR_DURATION_MS;
        const isPrimary = hasWorkOrderNumber(occurrence?.workOrderNumber);
        const usage = occurrence?.usage || component?.usage || component?.usageType || component?.usageLabel || 'N/A';
        const taskCode = component?.code?.code || 'N/A';
        const taskName = component?.code?.name || component?.name || 'Unnamed Task';
        const tooltip = `Task ${taskCode} - ${taskName}\nFrequency: ${usage}`;

        timelineItems.push({
          id: `${groupId}-${occurrence?.id || occurrence?.workOrderId || index}-${startMs}`,
          group: groupId,
          title: occurrence?.workOrderNumber || occurrence?.name || 'Occurrence',
          start_time: new Date(startMs),
          end_time: new Date(endMs),
          canMove: false,
          canResize: false,
          canChangeGroup: false,
          itemProps: {
            title: tooltip,
            style: {
              background: isOverdue ? '#dc3545' : isPrimary ? '#487fff' : '#737373',
              borderColor: isOverdue ? '#dc3545' : isPrimary ? '#487fff' : '#737373',
              color: '#fff',
              borderRadius: '4px',
            },
          },
        });
      });
    });

    const sortedGroups = [...groupedRows].sort((a, b) => {
      const valueA = String(a?.[sortConfig.key] || '').toLowerCase();
      const valueB = String(b?.[sortConfig.key] || '').toLowerCase();
      const comparison = valueA.localeCompare(valueB, undefined, { numeric: true, sensitivity: 'base' });
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });

    return { groups: sortedGroups, items: timelineItems };
  }, [components, sortConfig]);

  const renderSidebarHeader = () => (
    <div className='component-planner-sidebar-header'>
      <button type='button' className='planner-sort-button' onClick={() => toggleSort('assetNumber')}>
        Asset Number
        {sortConfig.key === 'assetNumber' && (
          <Icon icon={sortConfig.direction === 'asc' ? 'mdi:arrow-up' : 'mdi:arrow-down'} className='ms-1' />
        )}
      </button>
      <button type='button' className='planner-sort-button' onClick={() => toggleSort('taskLabel')}>
        Task Code - Task Name
        {sortConfig.key === 'taskLabel' && (
          <Icon icon={sortConfig.direction === 'asc' ? 'mdi:arrow-up' : 'mdi:arrow-down'} className='ms-1' />
        )}
      </button>
    </div>
  );

  const renderGroupRow = ({ group }) => (
    <div className='component-planner-sidebar-row'>
      <span className='text-truncate'>{group?.assetNumber || '-'}</span>
      <span className='text-truncate'>{group?.taskLabel || '-'}</span>
    </div>
  );

  return (
    <div className='card h-100 p-0 component-planner-card'>
      <div className='card-header d-flex justify-content-between align-items-center'>
        <div className='d-flex flex-wrap align-items-top gap-2'>
          {renderFilterControls()}
          <div className='btn-group ms-3 mt-24' style={{ height: '45px' }}>
            <button className={`btn btn-sm ${viewMode === 'day' ? 'btn-primary' : 'btn-light'}`} onClick={() => setViewMode('day')}>Day</button>
            <button className={`btn btn-sm ${viewMode === 'week' ? 'btn-primary' : 'btn-light'}`} onClick={() => setViewMode('week')}>Week</button>
            <button className={`btn btn-sm ${viewMode === 'month' ? 'btn-primary' : 'btn-light'}`} onClick={() => setViewMode('month')}>Month</button>
            <button className={`btn btn-sm ${viewMode === 'year' ? 'btn-primary' : 'btn-light'}`} onClick={() => setViewMode('year')}>Year</button>
          </div>
          <div className='btn-group ms-2 mt-24' style={{ height: '45px' }}>
            <button className='btn btn-sm btn-light' onClick={() => adjustDate(-1)}>
              <Icon icon='mage:chevron-left' />
            </button>
            <button className='btn btn-sm btn-light' onClick={() => adjustDate(1)}>
              <Icon icon='mage:chevron-right' />
            </button>
          </div>
        </div>
      </div>
      <div className="timeline-scrollbar-wrapper m-16 d-flex align-items-center justify-content-end gap-3">
        <span className='text-sm text-muted fw-semibold'>Zoom</span>
        <input
          type='range'
          min={ZOOMBAR_MIN}
          max={ZOOMBAR_MAX}
          step='1'
          value={timelineZoomValue}
          onChange={handleTimelineZoomBar}
          className='timeline-scrollbar'
        />
        <span className='text-sm text-muted fw-semibold'>Scroll</span>
        <input
          type='range'
          min={SCROLLBAR_MIN}
          max={SCROLLBAR_MAX}
          step='1'
          value={timelineScrollValue}
          onChange={handleTimelineScrollBar}
          className='timeline-scrollbar'
        />
      </div>
      <div className='card-body p-0'
        style={{ height: 'calc(100vh - 265px)' }}>
        <div
          className='h-100 overflow-auto'
          ref={ganttRef}
          onMouseDown={handleGanttMouseDown}
        >
          {!loading && items.length === 0 && (
            <div className='d-flex align-items-center justify-content-center h-100 text-muted'>
              No component occurrences found for the selected filters.
            </div>
          )}
          {loading && (
            <div className='d-flex align-items-center justify-content-center h-100 text-muted'>
              Loading component planner...
            </div>
          )}
          {!loading && items.length > 0 && (
            <Timeline
              groups={groups}
              items={items}
              visibleTimeStart={visibleTime.start}
              visibleTimeEnd={visibleTime.end}
              sidebarWidth={420}
              lineHeight={40}
              itemHeightRatio={0.45}
              canMove={false}
              canResize={false}
              canChangeGroup={false}
              traditionalZoom={false}
              stackItems
              subHeaderLabelFormats={viewMode === 'week' ? {
                dayShort: 'ddd MMM',
                dayMedium: 'ddd MMM',
                dayLong: 'ddd MMM',
              } : undefined}
              sidebarContent={renderSidebarHeader()}
              groupRenderer={renderGroupRow}
              onTimeChange={(visibleTimeStart, visibleTimeEnd, updateScrollCanvas) => {
                const nextWindow = clampWindowToToday(
                  visibleTimeStart,
                  visibleTimeEnd,
                  visibleTime.end - visibleTime.start,
                );
                setVisibleTime(nextWindow);
                updateScrollCanvas(nextWindow.start, nextWindow.end);
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ComponentPlannerLayer;
